import axios from 'axios';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import {
  LearnerFinalSummaryReport,
  LearnerFinalSummaryResponse,
  LearnerFinalSummaryStatus,
} from 'redux/schemas/app/learner-final-summary';

const MISSING_REPORT_ERROR_MESSAGE = 'The AI takeaway finished generating, but no report was returned.';
const REPORT_NOT_AVAILABLE_ERROR_MESSAGE = 'Your AI takeaway is not available for this course yet.';

const normalizeLearnerFinalSummaryStatus = (
  rawStatus: string | null | undefined,
  available: boolean | null | undefined,
  hasReport: boolean,
) => {
  switch (rawStatus) {
    case 'queued':
      return LearnerFinalSummaryStatus.Queued;
    case 'in_progress':
      return LearnerFinalSummaryStatus.Processing;
    case 'completed':
      return (available || hasReport) ? LearnerFinalSummaryStatus.Ready : LearnerFinalSummaryStatus.Failed;
    case 'failed':
      return LearnerFinalSummaryStatus.Failed;
    case 'not_available':
      return LearnerFinalSummaryStatus.Failed;
    default:
      return null;
  }
};

/**
 * Fetch the learner's end-of-course AI takeaway for a course.
 *
 * The backend returns a status plus the report payload once it is ready. While
 * the report is still generating the backend returns `queued`/`in_progress`.
 * This thunk normalizes those values into the UI lifecycle (`queued`,
 * `processing`, `ready`, `failed`) and tolerates both `{ result: {...} }` and
 * top-level JSON responses from VentureShell.
 */
export const fetchLearnerFinalSummary = createAsyncThunk<LearnerFinalSummaryResponse, { catalogId: string }>(
  'FETCH_LEARNER_FINAL_SUMMARY',
  async (arg) => {
    const { data } = await axios.get(
      `/${arg.catalogId}/learning_assistants/final_summary.json`,
    );
    // Tolerate both `{ result: {...} }` and a top-level `{ status, report }` body.
    const rawResponse = (data?.result ?? data) as Partial<LearnerFinalSummaryResponse> & {
      available?: boolean;
      status?: string | null;
    };
    const hasReport = !!rawResponse?.report;
    const rawStatus: string | null | undefined = rawResponse?.status;
    const status = normalizeLearnerFinalSummaryStatus(
      rawStatus,
      rawResponse?.available,
      hasReport,
    );
    let errorMessage = rawResponse?.errorMessage ?? null;
    if (status === LearnerFinalSummaryStatus.Failed && !errorMessage) {
      errorMessage = rawStatus === 'not_available'
        ? REPORT_NOT_AVAILABLE_ERROR_MESSAGE
        : MISSING_REPORT_ERROR_MESSAGE;
    }

    const normalizedResponse: LearnerFinalSummaryResponse = {
      ...rawResponse,
      status,
      report: rawResponse?.report ?? null,
      errorMessage,
    };

    return normalizedResponse;
  },
);

/** Show or hide the full-screen takeaway takeover. */
export const setShowLearnerTakeaway = createAction<boolean>(
  'SET_SHOW_LEARNER_TAKEAWAY',
);

/** Apply a report delivered out-of-band via Pusher without a refetch. */
export const setLearnerFinalSummaryReady = createAction<LearnerFinalSummaryReport>(
  'SET_LEARNER_FINAL_SUMMARY_READY',
);

/** Mark that the one-time auto-open has happened this session. */
export const setLearnerTakeawayAutoShown = createAction<boolean>(
  'SET_LEARNER_TAKEAWAY_AUTO_SHOWN',
);
